import { fromString } from 'ezmoney';

import { getV2ApiCompanyIdBudgetDefinitionsBudgetDefinitionIdSpendings } from 'src/client/spendesk-api';
import { useInfiniteQuery } from 'src/core/api/hooks/useInfiniteQuery';
import { type InfiniteQueryState } from 'src/core/api/queryState';
import { useCompany } from 'src/core/modules/app/hooks/useCompany';

import { getApiKey } from './keys';
import { type BreakdownPart } from '../../models/breakdown';
import {
  type SpendingType,
  type BreakdownSpending,
} from '../../models/breakdownSpending';
import { type PeriodInterval } from '../../models/periodInterval';
import { type NodePath } from '../models/nodePath';
import { buildApiPeriod } from '../models/period';

const pageSize = 20;

type Parameters = {
  budgetDefinitionId: string;
  breakdownPart: BreakdownPart;
  spendingType: SpendingType;
  period: PeriodInterval;
  nodePath: NodePath;
};

export const useBudgetSpendingsQuery = (
  parameters: Parameters,
): InfiniteQueryState<BreakdownSpending[]> => {
  const company = useCompany();
  return useInfiniteQuery({
    key: getApiKey(['spendings', parameters]),
    getRequest: (page = 1) => ({
      type: 'custom',
      fetchFunction: async () => {
        const pageNumber =
          typeof page === 'string' ? Number.parseInt(page) : page;
        const viewPeriod = buildApiPeriod(parameters.period);
        const response =
          await getV2ApiCompanyIdBudgetDefinitionsBudgetDefinitionIdSpendings({
            path: {
              companyId: company.id,
              budgetDefinitionId: parameters.budgetDefinitionId,
            },
            query: {
              // TMP cast until API is fixed
              breakdownPart: parameters.breakdownPart as 'used',
              spendingType: parameters.spendingType,
              pageNumber,
              pageSize,
              nodePath: parameters.nodePath.join(','),
              viewStart: viewPeriod.start,
              viewEnd: viewPeriod.end,
            },
          });
        if (response.statusCode !== 200) {
          throw new Error('Failed to get budget spendings');
        }
        return response.body;
      },
    }),
    getNextPageParam(lastPage, allPages) {
      // FIXME: Use a flag returned by the API to know if there is more pages
      // Currently, it is not possible to check with
      // lastPage.length === pageSize because of the `hack` aggregation post
      // fetch in the backend.
      if (lastPage.length === 0) {
        return undefined;
      }
      return allPages.length + 1;
    },
    reshapeData: (spendings) =>
      spendings.map<BreakdownSpending>((spending) => ({
        ...spending,
        date: new Date(spending.date),

        // Amounts
        initialGlobalAmount:
          spending.initialGlobalAmount !== undefined
            ? fromString(spending.initialGlobalAmount)
            : undefined,
        globalAmount: fromString(spending.globalAmount),
        amount: fromString(spending.amount),
        grossAmount: fromString(spending.grossAmount),

        document: spending.document
          ? {
              attachedAt: '', // FIXME API v2 missing field
              filename: spending.document.filename,
              mediaType: spending.document.mediaType,
            }
          : undefined,
      })),
  });
};
